<template>
  <div class="nativeAppContent pushOptInBanner" v-if="isVisible">
    <div class="native-banner-inner">
      <q-btn
        flat
        round
        dense
        icon="close"
        color="white"
        size="sm"
        class="native-banner-close"
        @click="remindLater"
        aria-label="Dismiss"
      />
      <div class="row items-center no-wrap q-gutter-sm">
        <div class="pushOptInBadge row items-center justify-center">
          <SvgIcon color="white" size="1rem" icon="notification-default" />
        </div>
        <div class="col">
          <div class="text-body2 text-weight-bold line-height-1">
            Never Miss a Reward
          </div>
          <div class="text-caption text-grey-7 line-height-normal">
            Turn on notifications for the latest deals, rewards, and exclusive
            {{ getCompanyName() }} offers.
          </div>
        </div>
        <q-btn
          unelevated
          no-caps
          dense
          color="primary"
          label="Turn On"
          padding="6px 16px"
          @click="goToSettings"
        />
      </div>
      <div class="row items-center justify-end q-gutter-md q-pt-sm">
        <q-btn
          unelevated
          no-caps
          flat
          dense
          size="12px"
          padding="0"
          color="grey-7"
          label="Remind Me Later"
          class="text-underline text-weight-regular no-hover"
          @click="remindLater"
        />
        <q-btn
          unelevated
          no-caps
          flat
          dense
          size="12px"
          padding="0"
          color="grey-7"
          label="Don't Ask Again"
          class="text-underline text-weight-regular no-hover"
          @click="noThanks"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Platform } from "quasar";
import { useCommonStore } from "src/stores/common-store";
import { useUserInfo } from "src/stores/user-store";
import { usePersistedStore } from "src/stores/persisted-store";
import { getCompanyName } from "src/utils/commonFunction";
import {
  canShowPushOptInPrompt,
  dismissPushOptInPrompt,
} from "src/utils/pushNotificationOptIn";

const route = useRoute();
const router = useRouter();
const commonStore = useCommonStore();
const userStore = useUserInfo();
const persistedStore = usePersistedStore();

const customerId = computed(() => userStore?.user_info?.id);
// Mounted globally in App.vue, so it has to hide itself everywhere but the
// dashboard. The install banner owns the same fixed slot above the footer.
const isHome = computed(() => route?.name === "Home");
const eligible = ref(false);
const checked = ref(false);
// The flags still off, passed on to the settings screen to highlight.
const offFlags = ref([]);
const isVisible = computed(
  () => eligible.value && isHome.value && !commonStore.showInstallAppBanner
);

// The dashboard is reached both by a cold start and by navigation, and the
// customer may only log in later, so re-evaluate until we get one clean run.
watch(
  () => [route?.name, persistedStore.isLoggedIn, customerId.value],
  () => checkEligibility(),
  { immediate: true }
);

async function checkEligibility() {
  if (checked.value) return;
  if (!isHome.value || !persistedStore.isLoggedIn || !customerId.value) return;
  if (!canShowPushOptInPrompt(customerId.value)) return;

  checked.value = true;
  const res = await userStore.getConsentTopics();
  // A false status means the preferences are not available for this customer
  // (e.g. age verification pending), so there is nothing to opt into.
  if (!res || !res.status) return;
  // Texts and emails come from the user details API, push from the consent
  // topics; prompt while any one of the three is still off.
  const flags = {
    can_text: userStore.user_info?.can_text,
    can_email: userStore.user_info?.can_email,
    can_push_notification: userStore.consent_settings?.can_push_notification,
  };
  offFlags.value = Object.keys(flags).filter((flag) => !flags[flag]);
  if (!offFlags.value.length) return;

  eligible.value = true;
}

function closeBanner() {
  eligible.value = false;
}

// Nothing is persisted: the banner returns on the next visit for as long as
// any of can_text, can_email or can_push_notification stays 0.
function remindLater() {
  closeBanner();
}

function noThanks() {
  dismissPushOptInPrompt(customerId.value);
  closeBanner();
}

// The settings screen owns the preference itself, so just hand the customer
// over to it. Mobile has a dedicated screen; on web the same toggles live in
// the Notifications tab of Edit Profile.
function goToSettings() {
  closeBanner();
  // Check again when the customer comes back to the dashboard, so the banner
  // returns if they left without turning everything on.
  checked.value = false;
  const highlight = offFlags.value.join(",");
  if (Platform.is.mobile) {
    router.push({ name: "UserSettings", query: { highlight } });
    return;
  }
  router.push({
    name: "EditProfile",
    query: { tab: "notifications", highlight },
  });
}
</script>

<style lang="scss" scoped>
/* Sits on the shared .nativeAppContent banner shell so it matches the
   "Download the App" banner; only the tighter padding and the leading badge
   are bespoke, to keep this nudge lighter than the install prompt. */
.pushOptInBanner {
  .native-banner-inner {
    padding: 12px;
  }

  /* Clear the mobile bottom bar, which is
     min-height: calc(env(safe-area-inset-bottom) + 79px), plus the shell's
     own 16px gap. */
  @media (max-width: $breakpoint-sm-max) {
    bottom: calc(env(safe-area-inset-bottom) + 95px);
  }
}

.pushOptInBadge {
  width: 2rem;
  height: 2rem;
  flex: 0 0 2rem;
  border-radius: 50%;
  background: var(--q-primary);
}
</style>
